import React from 'react';
import { LogOut } from 'lucide-react';
import { User } from '../types';
import { ProviderIcon } from './ProviderIcon';

interface UserProfileProps {
  user: User;
  onSignOut: () => void;
  isDarkMode: boolean;
}

export const UserProfile: React.FC<UserProfileProps> = ({ user, onSignOut, isDarkMode }) => {
  const providerName = user.provider === 'discord' ? 'Discord' : 'Google';
  
  // Determine subscription tier (support both old and new format)
  const tier = user.subscriptionTier || (user.isMax ? 'max' : user.isPro ? 'pro' : 'free');
  const isMax = tier === 'max';
  const isPro = tier === 'pro';
  const hasSubscription = isMax || isPro;
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl ${
      isMax
        ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-2 border-purple-500'
        : isPro 
        ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-2 border-blue-500'
        : isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
    }`}>
      <div className="relative">
        <img 
          src={user.picture} 
          alt={user.username || user.name}
          className={`w-10 h-10 rounded-full ${
            isMax ? 'ring-2 ring-purple-500' : isPro ? 'ring-2 ring-blue-500' : ''
          }`}
          referrerPolicy="no-referrer"
        />
        {hasSubscription && (
          <div className={`absolute -bottom-1 -right-1 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full ${
            isMax ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'
          }`}>
            {isMax ? 'MAX' : 'PRO'}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-sm font-medium truncate ${
            isMax
              ? 'bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent'
              : isPro 
              ? 'bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent'
              : isDarkMode ? 'text-gray-200' : 'text-gray-800'
          }`}>
            {user.username || user.name}
          </p>
          {hasSubscription && (
            <span className={`text-[10px] font-bold text-white px-1.5 py-0.5 rounded ${
              isMax ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'
            }`}>
              {isMax ? 'MAX' : 'PRO'}
            </span>
          )}
        </div>
        <p className={`text-xs truncate flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <ProviderIcon 
            provider={user.provider as 'discord' | 'google'} 
            size={14}
            className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}
          />
          {providerName}
        </p>
      </div>
      <button
        onClick={onSignOut}
        className={`p-2 rounded-lg transition-colors ${
          isDarkMode 
            ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
            : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
        }`}
        title="Sign out"
      >
        <LogOut size={18} />
      </button>
    </div>
  );
};
